import React, { useRef } from 'react';
import type { CanvasComponent as CanvasComponentType, ComponentStyle, DropdownOption, TableColumn, TableRow } from '../../../types/project';
import styles from './CanvasComponent.module.css';

// ── Props ─────────────────────────────────────────────────────────────────────

type CanvasComponentProps = {
  component: CanvasComponentType;
  selected: boolean;
  onSelect: (id: string) => void;
  onMove: (id: string, dx: number, dy: number) => void;
  onRemove: (id: string) => void;
};

// ── Inner renderers per component type ───────────────────────────────────────

function LabelRenderer({ label }: { label: string }): React.ReactElement {
  return <span className={styles.labelText}>{label || 'Label'}</span>;
}

function ButtonRenderer({ label }: { label: string }): React.ReactElement {
  return <button className={styles.buttonInner} tabIndex={-1}>{label || 'Button'}</button>;
}

function TextInputRenderer({ label, placeholder }: { label: string; placeholder?: string }): React.ReactElement {
  return (
    <div className={styles.inputWrapper}>
      {label && <label className={styles.inputLabel}>{label}</label>}
      <input
        className={styles.inputInner}
        type="text"
        placeholder={placeholder || 'Enter text…'}
        readOnly
        tabIndex={-1}
      />
    </div>
  );
}

function JsonViewerRenderer(): React.ReactElement {
  const sample = JSON.stringify({ status: 'ok', data: { key: 'value' } }, null, 2);
  return (
    <div className={styles.jsonViewer}>
      <div className={styles.jsonViewerHeader}>JSON Viewer</div>
      <pre className={styles.jsonViewerPre}>{sample}</pre>
    </div>
  );
}

function DropdownRenderer({
  label,
  placeholder,
}: {
  label: string;
  placeholder?: string;
}): React.ReactElement {
  return (
    <div className={styles.inputWrapper}>
      {label && <label className={styles.inputLabel}>{label}</label>}
      <select className={styles.inputInner} disabled tabIndex={-1}>
        <option value="">{placeholder || 'Select an option'}</option>
      </select>
    </div>
  );
}

function TextAreaRenderer({ label, placeholder }: { label: string; placeholder?: string }): React.ReactElement {
  return (
    <div className={styles.inputWrapper}>
      {label && <label className={styles.inputLabel}>{label}</label>}
      <textarea className={styles.textAreaInner} placeholder={placeholder || 'Enter multiple lines…'} readOnly tabIndex={-1} />
    </div>
  );
}

function CheckboxRenderer({ label, checked }: { label: string; checked: boolean }): React.ReactElement {
  return (
    <label className={styles.choiceRow}>
      <input type="checkbox" checked={checked} readOnly tabIndex={-1} />
      <span>{label || 'Checkbox'}</span>
    </label>
  );
}

function RadioGroupRenderer({ label, options, value }: { label: string; options: DropdownOption[]; value: string }): React.ReactElement {
  return (
    <fieldset className={styles.radioGroup}>
      {label && <legend>{label}</legend>}
      {(options.length > 0 ? options : [{ label: 'Option', value: 'option' }]).map((option) => (
        <label key={option.value} className={styles.choiceRow}>
          <input type="radio" checked={option.value === value} readOnly tabIndex={-1} />
          <span>{option.label}</span>
        </label>
      ))}
    </fieldset>
  );
}

function StatusPanelRenderer({ label, message, status }: { label: string; message: string; status: string }): React.ReactElement {
  return (
    <div className={styles.statusPanel} data-status={status}>
      {label && <strong>{label}</strong>}
      <span>{message || 'Ready'}</span>
    </div>
  );
}

function ContainerRenderer({ label, body }: { label: string; body: string }): React.ReactElement {
  return (
    <section className={styles.card}>
      {label && <div className={styles.cardTitle}>{label}</div>}
      <div className={styles.cardBody}>{body || 'Card content'}</div>
    </section>
  );
}

function TableRenderer({
  label,
  columns,
  rows,
  disabled,
}: {
  label: string;
  columns: TableColumn[];
  rows: TableRow[];
  disabled: boolean;
}): React.ReactElement {
  // Derive columns from first-row keys if columns is empty
  const effectiveCols: TableColumn[] =
    columns.length > 0
      ? columns
      : rows.length > 0
        ? Object.keys(rows[0]).map((k) => ({ key: k, header: k }))
        : [];

  return (
    <div className={[styles.tableWrapper, disabled ? styles.tableDisabled : ''].join(' ')}>
      {label && <div className={styles.tableLabel}>{label}</div>}
      {effectiveCols.length === 0 && rows.length === 0 ? (
        <div className={styles.tableEmpty}>No data</div>
      ) : (
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                {effectiveCols.map((col) => (
                  <th key={col.key} className={styles.tableTh} style={col.width ? { width: col.width, minWidth: col.width } : undefined}>{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    className={styles.tableTd}
                    colSpan={effectiveCols.length || 1}
                    style={{ textAlign: 'center', color: '#8b949e' }}
                  >
                    No rows
                  </td>
                </tr>
              ) : (
                rows.map((row, ri) => (
                  <tr key={ri}>
                    {effectiveCols.map((col) => (
                      <td key={col.key} className={styles.tableTd} style={col.width ? { width: col.width, minWidth: col.width } : undefined}>
                        {renderCellValue(row[col.key])}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Converts a cell value to a display string per the step spec. */
function renderCellValue(value: unknown): string {
  if (value === undefined) return '';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return '[serialisation error]';
  }
}

// ── Drag-to-move state ────────────────────────────────────────────────────────

type DragState = {
  startMouseX: number;
  startMouseY: number;
};

// ── Main component ────────────────────────────────────────────────────────────

function CanvasComponent({
  component,
  selected,
  onSelect,
  onMove,
  onRemove,
}: CanvasComponentProps): React.ReactElement {
  const { id, type, position, size, properties } = component;
  const label = typeof properties.label === 'string' ? properties.label : '';
  const placeholder = typeof properties.placeholder === 'string' ? properties.placeholder : undefined;

  const appearance = properties.style && typeof properties.style === 'object'
    ? properties.style as ComponentStyle
    : {};

  const dragState = useRef<DragState | null>(null);

  // ── Pointer-based drag-to-move ────────────────────────────────────────────

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Only drag on left button; don't initiate drag on the delete button
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('[data-remove]')) return;

    e.stopPropagation();
    onSelect(id);

    dragState.current = {
      startMouseX: e.clientX,
      startMouseY: e.clientY,
    };

    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return;

    const dx = e.clientX - dragState.current.startMouseX;
    const dy = e.clientY - dragState.current.startMouseY;

    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
      onMove(id, dx, dy);
      dragState.current = {
        startMouseX: e.clientX,
        startMouseY: e.clientY,
      };
    }
  };

  const handlePointerUp = () => {
    dragState.current = null;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={[styles.wrapper, selected ? styles.selected : ''].join(' ')}
      data-custom-font-size={appearance.fontSize !== undefined ? 'true' : undefined}
      data-custom-text-color={appearance.textColor ? 'true' : undefined}
      data-custom-background-color={appearance.backgroundColor ? 'true' : undefined}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: type === 'JsonViewer' || type === 'TextInput' || type === 'Table' ? 'auto' : size.height,
        minHeight: size.height,
        ...(appearance.fontSize !== undefined ? { fontSize: appearance.fontSize } : {}),
        ...(appearance.textColor ? { color: appearance.textColor } : {}),
        ...(appearance.backgroundColor ? { backgroundColor: appearance.backgroundColor } : {}),
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={(e) => { e.stopPropagation(); onSelect(id); }}
    >
      {/* Component name badge */}
      <span className={styles.typeBadge}>{type}</span>

      {/* Delete button — shown when selected */}
      {selected && (
        <button
          className={styles.removeBtn}
          data-remove="true"
          onClick={(e) => { e.stopPropagation(); onRemove(id); }}
          title="Remove component"
        >
          ✕
        </button>
      )}

      {/* Content */}
      <div className={type === 'Table' ? styles.tableContent : styles.content}>
        {type === 'Label'      && <LabelRenderer     label={label} />}
        {type === 'Button'     && <ButtonRenderer     label={label} />}
        {type === 'TextInput'  && <TextInputRenderer  label={label} placeholder={placeholder} />}
        {type === 'JsonViewer' && <JsonViewerRenderer />}
        {type === 'Dropdown'   && <DropdownRenderer   label={label} placeholder={placeholder} />}
        {type === 'TextArea' && <TextAreaRenderer label={label} placeholder={placeholder} />}
        {type === 'Checkbox' && (
          <CheckboxRenderer label={label} checked={properties.defaultValue === true} />
        )}
        {type === 'RadioGroup' && (
          <RadioGroupRenderer
            label={label}
            options={Array.isArray(properties.options) ? (properties.options as DropdownOption[]) : []}
            value={typeof properties.value === 'string' ? properties.value : ''}
          />
        )}
        {type === 'StatusPanel' && (
          <StatusPanelRenderer
            label={label}
            message={typeof properties.defaultValue === 'string' ? properties.defaultValue : ''}
            status={typeof properties.status === 'string' ? properties.status : 'info'}
          />
        )}
        {type === 'Container' && (
          <ContainerRenderer
            label={label}
            body={typeof properties.defaultValue === 'string' ? properties.defaultValue : ''}
          />
        )}
        {type === 'Table'      && (
          <TableRenderer
            label={label}
            columns={Array.isArray(properties.columns) ? (properties.columns as TableColumn[]) : []}
            rows={Array.isArray(properties.rows) ? (properties.rows as TableRow[]) : []}
            disabled={properties.disabled === true}
          />
        )}
      </div>
    </div>
  );
}

export default CanvasComponent;
